import LayoutContainer from 'components/Common/LayoutContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getCanLoyaltyEnrollExistingUser from 'loyalty/selectors/getCanLoyaltyEnrollExistingUser'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { matchPath } from 'react-router'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import styled from 'styled-components'

const Container = styled.div`
  padding-right: ${rem(16)};
  padding-block: ${rem(12)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const BannerDismissButton = styled(IconButton)`
  margin: 0 0 0 ${rem(24)};
`

const BANNER_KEY = 'loyalty-upgrade-promo'
const BANNER_DISMISSAL_PERIOD_DAYS = 14

const noBannerRoutes: Array<string> = [
  '/:regionCode/trip-planner/generate-trip',
]

export default function LoyaltyUpgradePromoBanner() {
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const canLoyaltyEnrollExistingUser = useAppSelector(getCanLoyaltyEnrollExistingUser)
  const isLoadingLoyaltyAccount = useAppSelector((state) => state.loyalty.account.fetching)

  const [tier] = useCurrentLoyaltyTier()

  const message = useMemo(() => {
    switch (tier) {
      case 'silver':
        return 'Free upgrades now available to Silver members'
      case 'gold':
        return 'Enjoy an extra upgrade for your stay every year!'
      case 'bronze':
      case 'platinum':
      default:
        return ''
    }
  }, [tier])

  const ctaMessage = useMemo(() => {
    switch (tier) {
      case 'bronze':
      case 'silver':
      case 'gold':
      case 'platinum':
      default:
        return 'View all offers'
    }
  }, [tier])

  const ctaUrl = useMemo(() => {
    const filters: App.OfferListFilters = {
      destinations: [ANYWHERE_PLACE_ID],
      destinationNames: [ANYWHERE_NAME],
      loyalty: ['Free Room Upgrade'],
    }
    switch (tier) {
      case 'bronze':
      case 'silver':
      case 'gold':
      case 'platinum':
      default:
        return `/search?${getSearchQueryFromFilter(filters)}`
    }
  }, [tier])

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(isBannerDismissed(BANNER_KEY))
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setIsDismissed(isBannerDismissed(BANNER_KEY))
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const onDismiss = useCallback(() => {
    setIsDismissed(true)
    // Dismiss endDate in BANNER_DISMISSAL_PERIOD_DAYS days from now
    recordSiteBannerDismissal(BANNER_KEY, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
  }, [])

  const isEligible = useMemo(() => {
    return isLoggedIn && isLoyaltyEnabled && (tier === 'silver' || tier === 'gold') && !isLoadingLoyaltyAccount && !canLoyaltyEnrollExistingUser
  }, [isLoggedIn, isLoyaltyEnabled, tier, isLoadingLoyaltyAccount, canLoyaltyEnrollExistingUser])

  const hide = useMemo(() => {
    return !!matchPath(currentPath, { path: noBannerRoutes })
  }, [currentPath])

  if (!isEligible || isDismissed || hide) return null

  return (
    <CSSBreakpoint min="tablet">
      <Container className={`tier-${tier}`}>
        <LayoutContainer>
          <Group direction="horizontal" gap={12} verticalAlign="center" horizontalAlign="center">
            <Group direction="horizontal" gap={12} horizontalAlign="center" verticalAlign="center" wrap="wrap">
              <LoyaltyProductLogo height={24} />
              <LoyaltyTierPill tier={tier} kind="primary" icon={null}>New benefits</LoyaltyTierPill>
              <BodyText variant="medium">{message}</BodyText>
              <TextLink size="medium" to={ctaUrl}>{ctaMessage}</TextLink>
            </Group>
            <BannerDismissButton
              kind="tertiary"
              size="small"
              onClick={onDismiss}
            >
              <LineTimesIcon />
            </BannerDismissButton>
          </Group>
        </LayoutContainer>
      </Container>
    </CSSBreakpoint>
  )
}
